import type { PreloadDistance } from '~/composables/useCardPreloadBudget';

import { notifyCardPreloadSignal } from '~/composables/useCardPreloadBudget';
import { getSavedBandwidthEstimate } from '~/composables/useHlsPlayer/shakaLoader';
import { useIndexScrollVelocity } from '~/composables/useIndexScrollVelocity';

// Janela assimetrica (mais a frente do que atras) e nada atras em rolagem
// rapida: cards atras do gesto nunca serao vistos.

/** Cards a frente do gesto que ganham pre-carga (mesma janela da Mux). */
const AHEAD_WINDOW = 5;
/** Atras so o vizinho imediato: voltar um card e comum, voltar seis nao. */
const BEHIND_WINDOW = 1;
// Rede lenta encurta a janela: nao dividir o cano em cinco onde nao cabe um.
const AHEAD_WINDOW_SLOW_NETWORK = 2;
const SLOW_NETWORK_BPS = 2_000_000;

export interface CarouselPreloadWindow {
	direction: Readonly<Ref<'forward' | 'backward'>>;
	isFastScrolling: Readonly<Ref<boolean>>;
	/** Distancia do foco, ou `null` quando fora da janela (nao abre cadeia). */
	preloadDistanceFor: (index: number) => PreloadDistance | null;
}

/**
 * `currentIndex` null = lista ainda nao paginou: a janela nao opina e todo
 * card recebe distancia 0.
 */
export function useCarouselPreloadWindow(
	currentIndex: () => number | null | undefined
): CarouselPreloadWindow {
	const { direction, isFastScrolling, report } = useIndexScrollVelocity();

	function computeAheadLimit(): number {
		if (!import.meta.client) return AHEAD_WINDOW;
		const bps = getSavedBandwidthEstimate();
		// Estimativa desconhecida nao e tratada como lenta (mesmo criterio do teto).
		return bps !== null && bps < SLOW_NETWORK_BPS
			? AHEAD_WINDOW_SLOW_NETWORK
			: AHEAD_WINDOW;
	}

	// Ligado ao indice, nao a cada card: sem isto todo card visivel no v-for
	// batia localStorage a cada render em vez de uma vez por troca real.
	const aheadLimitValue = ref(computeAheadLimit());

	watch(
		currentIndex,
		index => {
			if (index == null) return;
			const changed = report(index);
			if (!changed) return;
			// Carrossel andou = evidencia de intencao: libera o teto especulativo.
			notifyCardPreloadSignal();
			aheadLimitValue.value = computeAheadLimit();
		},
		{ immediate: true }
	);

	function preloadDistanceFor(index: number): PreloadDistance | null {
		const current = currentIndex();
		if (current == null) return 0;

		const offset = index - current;
		// Reorienta para o sentido do gesto: "a frente" e para onde ele vai.
		const ahead = direction.value === 'forward' ? offset : -offset;

		if (ahead < 0) {
			if (isFastScrolling.value) return null;
			return -ahead <= BEHIND_WINDOW ? -ahead : null;
		}
		return ahead <= aheadLimitValue.value ? ahead : null;
	}

	return { direction, isFastScrolling, preloadDistanceFor };
}
